import { useEffect, useRef, useState } from "react";

/**
 * The order pad's motion, in one place.
 *
 * Everything here is decoration. Nothing in this file decides what an order
 * contains, what it costs or when it is sent — remove the file and the screen
 * still works, it just stops moving. That is on purpose: a till is used at
 * speed by people who are not looking at it, so motion may never stand between
 * a tap and what the tap does.
 *
 * Three rules it keeps to:
 *
 *  1. Only `transform` and `opacity` are animated. Neither causes the browser
 *     to lay the page out again, so ten taps in two seconds stay smooth on the
 *     cheap machines these tills run on.
 *  2. Nothing lasts longer than about a third of a second, and everything can
 *     be interrupted — tapping the same card five times fast never queues up a
 *     backlog of animations to sit through.
 *  3. A machine set to "reduce motion" gets none of it. Every helper checks,
 *     and the stylesheet turns itself off as well.
 *
 * The one thing on screen that is NEW rather than merely moving is the red
 * count on a menu card, which is the badge the design demo showed.
 */

const STYLE_ID = "dalabtire-orders-animations";

/** Where a flying item lands: the cart list gives itself this id. */
export const CART_DROP_ID = "dt-cart-drop";

const CSS = `
/* ── the menu card ──────────────────────────────────────────────────────── */
.dt-card{
  position:relative;
  overflow:hidden;
  transition:transform .18s cubic-bezier(.34,1.56,.64,1), box-shadow .18s ease;
  will-change:transform;
}
.dt-card:hover{ transform:translateY(-2px); box-shadow:0 6px 18px rgba(20,24,29,.10); }
.dt-card:active{ transform:scale(.96); }

/* the ripple spreads from wherever the finger landed */
.dt-ripple{
  position:absolute;
  border-radius:999px;
  background:rgba(246,72,41,.16);
  transform:translate(-50%,-50%) scale(0);
  pointer-events:none;
  animation:dt-ripple .5s cubic-bezier(.2,.8,.25,1) forwards;
}
@keyframes dt-ripple{ to{ transform:translate(-50%,-50%) scale(1); opacity:0; } }

/* the red count in the corner — the only new thing on screen */
.dt-badge{
  position:absolute;
  top:8px;
  right:8px;
  min-width:26px;
  height:26px;
  padding:0 7px;
  border-radius:999px;
  background:#F64829;
  color:#fff;
  font-size:14px;
  font-weight:bold;
  line-height:26px;
  text-align:center;
  box-shadow:0 2px 6px rgba(246,72,41,.4);
  transform:scale(0);
  transition:transform .28s cubic-bezier(.34,1.56,.64,1);
  pointer-events:none;
  z-index:2;
}
.dt-badge-show{ transform:scale(1); }
.dt-badge-bump{ animation:dt-bump .32s cubic-bezier(.34,1.56,.64,1); }
@keyframes dt-bump{ 0%{transform:scale(1)} 40%{transform:scale(1.3)} 100%{transform:scale(1)} }

/* the menu arriving, one card after another */
.dt-in{ animation:dt-in .34s cubic-bezier(.2,.8,.25,1) both; }
@keyframes dt-in{ from{ opacity:0; transform:translateY(10px) scale(.97); } to{ opacity:1; transform:none; } }

/* the copy that flies into the cart */
.dt-ghost{
  position:fixed;
  z-index:2000;
  border-radius:6px;
  overflow:hidden;
  pointer-events:none;
  box-shadow:0 8px 24px rgba(20,24,29,.25);
}

/* ── the cart ───────────────────────────────────────────────────────────── */
.dt-line{ border-radius:8px; }
.dt-line-enter{ animation:dt-line-in .34s cubic-bezier(.2,.8,.25,1) both; }
@keyframes dt-line-in{ from{ opacity:0; transform:translateX(26px); } to{ opacity:1; transform:none; } }
.dt-line-flash{ animation:dt-flash .9s cubic-bezier(.2,.8,.25,1); }
@keyframes dt-flash{ from{ background:#FFF1ED; } to{ background:transparent; } }

.dt-tick{ animation:dt-tick .3s cubic-bezier(.34,1.56,.64,1); }
@keyframes dt-tick{ 0%{transform:translateY(0)} 45%{transform:translateY(-5px);opacity:.4} 100%{transform:translateY(0)} }

.dt-step{ transition:transform .15s cubic-bezier(.34,1.56,.64,1); }
.dt-step:active{ transform:scale(.85); }

/* the black pill that slides between Dine-in / Takeaway / Delivery */
.dt-pill{
  position:absolute;
  top:0;
  left:0;
  border-radius:5px;
  background:black;
  transition:transform .28s cubic-bezier(.34,1.56,.64,1), width .28s cubic-bezier(.34,1.56,.64,1);
  pointer-events:none;
}

/* Place Order */
.dt-pay{ transition:transform .15s cubic-bezier(.34,1.56,.64,1), background .3s ease; }
.dt-pay:active{ transform:scale(.98); }
.dt-pay-done{ background:#16A34A !important; }

/* ── somebody who asked for less movement gets none of it ───────────────── */
@media (prefers-reduced-motion: reduce){
  .dt-card,.dt-card:hover,.dt-card:active,.dt-step,.dt-step:active,.dt-pay,.dt-pay:active{
    transition:none;
    transform:none;
    box-shadow:none;
  }
  .dt-pill{ transition:none; }
  .dt-in,.dt-line-enter,.dt-line-flash,.dt-tick,.dt-badge-bump,.dt-ripple{ animation:none; }
  .dt-badge{ transition:none; }
}
`;

/** True when the machine asks for as little movement as possible. */
export function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Put the stylesheet in the page, once, however many components ask for it.
 *
 * A <style> rendered inside a component would be torn out and put back every
 * time that component unmounted — mid-animation, on a screen the cashier is
 * still using.
 */
export function useOrderAnimations() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(STYLE_ID)) return;

    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.appendChild(document.createTextNode(CSS));
    document.head.appendChild(el);
  }, []);
}

/**
 * Send a copy of the tapped picture curving into the cart.
 *
 * It is a throwaway clone on top of the page: nothing in the layout moves, and
 * if anything at all is missing — the element, the cart, an old browser with
 * no Web Animations — it simply does not happen and the order is unaffected.
 */
export function flyToCart(sourceEl) {
  if (prefersReducedMotion()) return;
  if (typeof document === "undefined") return;
  if (!sourceEl || typeof sourceEl.getBoundingClientRect !== "function") return;

  const target = document.getElementById(CART_DROP_ID);
  if (!target) return;

  const from = sourceEl.getBoundingClientRect();
  const to = target.getBoundingClientRect();
  if (!from.width || !to.width) return;

  const ghost = sourceEl.cloneNode(true);
  if (typeof ghost.animate !== "function") return;

  ghost.className = "dt-ghost";
  ghost.style.left = from.left + "px";
  ghost.style.top = from.top + "px";
  ghost.style.width = from.width + "px";
  ghost.style.height = from.height + "px";
  ghost.style.margin = "0";
  document.body.appendChild(ghost);

  // Aim at the top of the cart list, which is where the new line will appear.
  const dx = to.left + to.width / 2 - (from.left + from.width / 2);
  const dy = to.top + 40 - (from.top + from.height / 2);

  const remove = () => {
    if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
  };

  const run = ghost.animate(
    [
      { transform: "translate(0,0) scale(1)", opacity: 1, offset: 0 },
      {
        transform:
          "translate(" + dx * 0.55 + "px," + (dy * 0.25 - 60) + "px) scale(.6)",
        opacity: 0.95,
        offset: 0.55,
      },
      {
        transform: "translate(" + dx + "px," + dy + "px) scale(.12)",
        opacity: 0.15,
        offset: 1,
      },
    ],
    { duration: 520, easing: "cubic-bezier(.4,.1,.3,1)" }
  );

  run.onfinish = remove;
  run.oncancel = remove;
  // Belt and braces: a tab put to sleep mid-flight must not leave litter on
  // top of the screen.
  window.setTimeout(remove, 1200);
}

/**
 * A number that rolls to its new value instead of jumping to it.
 *
 * The figure returned is only for showing. Everything that adds up, gets sent
 * or gets printed keeps using the real value.
 */
export function useCountUp(value, duration) {
  const to = Number(value) || 0;
  const [shown, setShown] = useState(to);
  const shownRef = useRef(to);
  const frameRef = useRef(0);

  useEffect(() => {
    const from = shownRef.current;
    const ms = duration || 380;

    const settle = () => {
      shownRef.current = to;
      setShown(to);
    };

    if (from === to || prefersReducedMotion() || typeof window === "undefined") {
      settle();
      return undefined;
    }

    const start =
      window.performance && window.performance.now
        ? window.performance.now()
        : Date.now();

    const step = now => {
      const k = Math.min(1, (now - start) / ms);
      const eased = 1 - Math.pow(1 - k, 3);
      const next = from + (to - from) * eased;
      shownRef.current = next;
      setShown(next);
      if (k < 1) frameRef.current = window.requestAnimationFrame(step);
      else settle();
    };

    frameRef.current = window.requestAnimationFrame(step);

    return () => {
      // Interrupted: carry on from wherever it had got to, so a second change
      // mid-roll does not snap backwards.
      window.cancelAnimationFrame(frameRef.current);
    };
  }, [to, duration]);

  return shown;
}

/**
 * Which cart line just changed, and whether it is brand new.
 *
 * The cart itself says nothing about this — it is a list, and a list that has
 * grown by one looks exactly like a list that always had that item. So the
 * previous quantities are kept here and compared.
 *
 * Returns `{ id, fresh }`, and clears itself shortly after so the animation
 * does not replay on every unrelated render.
 */
export function useCartPulse(cart) {
  const [pulse, setPulse] = useState(null);
  const previous = useRef(null);

  useEffect(() => {
    const now = {};
    cart.forEach(item => {
      now[item.productId] = item.qty;
    });

    const before = previous.current;
    previous.current = now;

    // First render of a screen that already has a cart (an order opened for
    // editing) should not flash every line at once.
    if (!before) return undefined;

    let hit = null;
    let fresh = false;

    Object.keys(now).forEach(id => {
      if (before[id] === undefined) {
        hit = id;
        fresh = true;
      } else if (now[id] > before[id] && !fresh) {
        hit = id;
      }
    });

    if (!hit) return undefined;

    setPulse({ id: hit, fresh });
    const timer = window.setTimeout(() => setPulse(null), 1000);
    return () => window.clearTimeout(timer);
  }, [cart]);

  return pulse;
}
